// @upryzze/forms — safe-expression engine: evaluator (AST + context → value).
//
// Runtime contract: NEVER throws on bad/missing data. A missing field,
// division by zero, type mismatch, or unknown function yields `null` (the
// "no value" sentinel). Callers coerce: a computed NUMBER field takes the
// numeric result or shows blank; a `visibleWhen` boolean takes truthiness.
//
// This runs both client-side (Console renderer, live fill) and server-side
// (authoritative recompute on submit). Same code, same answer.

import { parse } from "./parser.js";

/**
 * @param {object|string} astOrSource Parsed AST, or a source string to parse.
 * @param {object} [context]
 * @param {Record<string, unknown>} [context.values] field name → value
 * @param {(field:string, attrName:string)=>unknown} [context.attr] catalog attr
 * @param {(group:string)=>Array<Record<string, unknown>>} [context.rows] repeatable rows
 * @returns {*} the value, or null when undefined/erroring.
 */
export function evaluate(astOrSource, context = {}) {
  let ast;
  try {
    ast = typeof astOrSource === "string" ? parse(astOrSource) : astOrSource;
  } catch {
    return null; // unparseable → no value (validate() is the gate for authoring)
  }
  try {
    const v = evalNode(ast, context);
    return v === undefined ? null : v;
  } catch {
    return null;
  }
}

/** Evaluate to a finite number, or null. Convenience for computed fields. */
export function evaluateNumber(astOrSource, context) {
  return toNumberOrNull(evaluate(astOrSource, context));
}

/** Evaluate to a boolean (truthiness). Convenience for `visibleWhen`. */
export function evaluateBoolean(astOrSource, context) {
  return truthy(evaluate(astOrSource, context));
}

// ── helpers ──
function toNumberOrNull(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  if (typeof v === "boolean") return v ? 1 : 0;
  return null;
}

function truthy(v) {
  if (v === null || v === undefined) return false;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return Number.isFinite(v) && v !== 0;
  if (typeof v === "string") return v.length > 0;
  return true;
}

// ── node evaluation ──
function evalNode(n, ctx) {
  switch (n.type) {
    case "num":
    case "str":
    case "bool":
      return n.value;
    case "null":
      return null;

    case "field": {
      const values = ctx.values || {};
      // Own-property only — never walk the prototype chain.
      return Object.prototype.hasOwnProperty.call(values, n.name)
        ? values[n.name]
        : null;
    }

    case "unary": {
      if (n.op === "!") return !truthy(evalNode(n.arg, ctx));
      // unary minus
      const x = toNumberOrNull(evalNode(n.arg, ctx));
      return x === null ? null : -x;
    }

    case "logical": {
      const left = evalNode(n.left, ctx);
      if (n.op === "&&") return truthy(left) ? truthy(evalNode(n.right, ctx)) : false;
      // ||
      return truthy(left) ? true : truthy(evalNode(n.right, ctx));
    }

    case "ternary":
      return truthy(evalNode(n.test, ctx))
        ? evalNode(n.cons, ctx)
        : evalNode(n.alt, ctx);

    case "binary":
      return evalBinary(n, ctx);

    case "call":
      return evalCall(n, ctx);

    default:
      return null;
  }
}

function evalBinary(n, ctx) {
  const op = n.op;

  // Equality compares typed values predictably: numbers numerically,
  // everything else by string identity; null == null.
  if (op === "==" || op === "!=") {
    const a = evalNode(n.left, ctx);
    const b = evalNode(n.right, ctx);
    const eq = looseEquals(a, b);
    return op === "==" ? eq : !eq;
  }

  // All other operators are numeric. Non-numeric operand → null.
  const a = toNumberOrNull(evalNode(n.left, ctx));
  const b = toNumberOrNull(evalNode(n.right, ctx));
  if (a === null || b === null) return null;

  switch (op) {
    case "+":
      return a + b;
    case "-":
      return a - b;
    case "*":
      return a * b;
    case "/":
      return b === 0 ? null : a / b;
    case "%":
      return b === 0 ? null : a % b;
    case "<":
      return a < b;
    case "<=":
      return a <= b;
    case ">":
      return a > b;
    case ">=":
      return a >= b;
    default:
      return null;
  }
}

function looseEquals(a, b) {
  if (a === null || a === undefined) return b === null || b === undefined;
  if (b === null || b === undefined) return false;
  if (typeof a === "number" || typeof b === "number") {
    const na = toNumberOrNull(a);
    const nb = toNumberOrNull(b);
    if (na !== null && nb !== null) return na === nb;
  }
  return String(a) === String(b);
}

function evalCall(n, ctx) {
  switch (n.name) {
    case "min":
    case "max": {
      if (n.args.length === 0) return null; // validate() rejects this; stay total
      const nums = n.args.map((a) => toNumberOrNull(evalNode(a, ctx)));
      if (nums.some((x) => x === null)) return null;
      return n.name === "min" ? Math.min(...nums) : Math.max(...nums);
    }
    case "abs": {
      const x = toNumberOrNull(evalNode(n.args[0], ctx));
      return x === null ? null : Math.abs(x);
    }
    case "round": {
      const x = toNumberOrNull(evalNode(n.args[0], ctx));
      if (x === null) return null;
      const d = n.args.length > 1 ? toNumberOrNull(evalNode(n.args[1], ctx)) : 0;
      if (d === null) return null;
      const digits = Math.max(0, Math.min(10, Math.trunc(d)));
      const f = 10 ** digits;
      return Math.round(x * f) / f;
    }
    case "ceil": {
      const x = toNumberOrNull(evalNode(n.args[0], ctx));
      return x === null ? null : Math.ceil(x);
    }
    case "floor": {
      const x = toNumberOrNull(evalNode(n.args[0], ctx));
      return x === null ? null : Math.floor(x);
    }
    case "clamp": {
      const x = toNumberOrNull(evalNode(n.args[0], ctx));
      const lo = toNumberOrNull(evalNode(n.args[1], ctx));
      const hi = toNumberOrNull(evalNode(n.args[2], ctx));
      if (x === null || lo === null || hi === null) return null;
      return Math.min(Math.max(x, lo), hi);
    }
    case "attr": {
      const field = literalString(n.args[0]);
      const name = literalString(n.args[1]);
      if (field === null || name === null) return null;
      if (typeof ctx.attr !== "function") return null;
      const v = ctx.attr(field, name);
      return v === undefined ? null : v;
    }
    case "count": {
      const group = literalString(n.args[0]);
      if (group === null || typeof ctx.rows !== "function") return null;
      const rows = ctx.rows(group);
      return Array.isArray(rows) ? rows.length : 0;
    }
    case "sum": {
      const group = literalString(n.args[0]);
      const child = literalString(n.args[1]);
      if (group === null || child === null || typeof ctx.rows !== "function") {
        return null;
      }
      const rows = ctx.rows(group);
      if (!Array.isArray(rows)) return 0;
      let total = 0;
      for (const row of rows) {
        if (row && Object.prototype.hasOwnProperty.call(row, child)) {
          const v = toNumberOrNull(row[child]);
          if (v !== null) total += v;
        }
      }
      return total;
    }
    default:
      return null; // unknown function (validate() rejects these at authoring)
  }
}

// Data-accessor name args are string literals (enforced by validate()). The
// evaluator NEVER computes a name from runtime values — a non-literal here
// yields null, so no attacker-controlled string can reach the host resolver
// even if evaluate() runs on an unvalidated expression.
function literalString(arg) {
  return arg && arg.type === "str" ? arg.value : null;
}

export { toNumberOrNull, truthy };
